import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  Box,
  HStack,
} from "@chakra-ui/react";
import { CheckCircleIcon, WarningIcon } from "@chakra-ui/icons";
import PropTypes from "prop-types";
import { useState, useCallback } from "react";
import {
  fieldValidators,
  sanitizeInput,
  getPasswordStrength,
  validatePasteContent,
} from "../utils/inputValidators";

/**
 * ValidatedInput - Industry-standard validated input component
 * Provides comprehensive validation, error handling, and accessibility
 *
 * @param {Object} props
 * @param {string} props.name - Field name
 * @param {string} props.label - Field label
 * @param {any} props.value - Field value
 * @param {string} props.type - Input type (text, number, email, password, etc.)
 * @param {string} props.fieldType - Field type for validation (text, name, email, phone, age, currency, password, url, date, address, bio, number)
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.onBlur - Blur handler
 * @param {string} props.error - External error message
 * @param {boolean} props.isTouched - Whether field has been touched
 * @param {string} props.placeholder - Input placeholder
 * @param {string} props.helperText - Helper/hint text
 * @param {string} props.prefix - Left element (currency symbol, %, etc.)
 * @param {number} props.min - Minimum value
 * @param {number} props.max - Maximum value
 * @param {number} props.step - Step value for number inputs
 * @param {boolean} props.required - Whether field is required
 * @param {boolean} props.disabled - Whether field is disabled
 * @param {string} props.inputMode - Input mode for mobile keyboards
 * @param {Array} props.validationRules - Display rules as list items
 * @param {Object} props.validationOptions - Options for field-specific validation
 * @returns {JSX.Element}
 */
const ValidatedInput = ({
  name,
  label,
  value,
  type = "text",
  fieldType = "text",
  onChange,
  onBlur,
  error: externalError,
  isTouched,
  placeholder,
  helperText,
  prefix,
  min,
  max,
  step,
  required = false,
  disabled = false,
  inputMode,
  validationRules = [],
  validationOptions = {},
  showPasswordStrength = false,
  ...rest
}) => {
  const [internalError, setInternalError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState({ level: "none", score: 0 });

  const validator = fieldValidators[fieldType];
  const isError = !!((externalError || internalError) && isTouched);
  const isValid = !externalError && !internalError && isTouched && value !== "";
  const maxLength = validator?.maxLength || max;

  /**
   * Validate field value and update error state
   */
  const validateField = useCallback(
    (fieldValue) => {
      if (!validator) return;

      const validation = validator.validate(fieldValue, validationOptions);

      if (!validation.isValid) {
        setInternalError(validation.message);
      } else {
        setInternalError("");
      }

      // Update password strength if applicable
      if (fieldType === "password" && showPasswordStrength) {
        const strength = getPasswordStrength(fieldValue);
        setPasswordStrength(strength);
      }
    },
    [validator, fieldType, validationOptions, showPasswordStrength]
  );

  /**
   * Handle input change with real-time validation
   */
  const handleChange = useCallback(
    (e) => {
      let { value: newValue } = e.target;

      // For numeric fields, filter out non-numeric characters in real-time
      if (fieldType === "age" || fieldType === "number") {
        newValue = newValue.replace(/[^\d]/g, "");
        if (fieldType === "age") {
          newValue = newValue.substring(0, 3); // Max 3 digits for age
        }
      }

      // For currency, allow digits and decimal point
      if (fieldType === "currency") {
        newValue = newValue.replace(/[^\d.]/g, "");
        // Prevent multiple decimal points
        const parts = newValue.split(".");
        if (parts.length > 2) {
          newValue = parts[0] + "." + parts[1];
        }
      }

      // For name fields, filter special characters
      if (fieldType === "name") {
        newValue = newValue.replace(/[^a-zA-Z\s\-']/g, "");
      }

      // For phone, allow digits and common phone characters
      if (fieldType === "phone") {
        newValue = newValue.replace(/[^\d+\-().\s]/g, "");
      }

      // Enforce max length
      if (maxLength && newValue.length > maxLength) {
        newValue = newValue.substring(0, maxLength);
      }

      // Call parent onChange
      onChange({ target: { name, value: newValue } });

      // Validate if field has been touched
      if (isTouched) {
        validateField(newValue);
      }
    },
    [name, onChange, isTouched, fieldType, maxLength, validateField]
  );

  /**
   * Handle key press for additional validation
   */
  const handleKeyPress = useCallback(
    (e) => {
      if (validator && validator.onKeyPress) {
        validator.onKeyPress(e);
      }
    },
    [validator]
  );

  /**
   * Handle field blur - trim whitespace and validate
   */
  const handleBlur = useCallback(
    (e) => {
      let { value: blurValue } = e.target;

      // Trim whitespace for all text fields
      if (fieldType !== "number" && fieldType !== "age" && fieldType !== "currency") {
        blurValue = blurValue.trim();
      }

      // Sanitize input
      const sanitized = sanitizeInput(blurValue, fieldType);

      // Update value if sanitization changed it
      if (sanitized !== blurValue) {
        onChange({ target: { name, value: sanitized } });
      }

      // Validate on blur
      validateField(sanitized);

      // Call parent onBlur
      if (onBlur) {
        onBlur(e);
      }
    },
    [name, fieldType, onChange, onBlur, validateField]
  );

  /**
   * Handle paste events - validate pasted content
   */
  const handlePaste = useCallback(
    (e) => {
      const pastedText = (e.clipboardData || window.clipboardData).getData("text");
      const pasteValidation = validatePasteContent(pastedText, fieldType);

      if (!pasteValidation.isValid) {
        e.preventDefault();
        setInternalError(pasteValidation.message);
      }
    },
    [fieldType]
  );

  /**
   * Get field-specific max length
   */
  const getMaxLength = () => {
    if (fieldType === "email") return 254;
    if (fieldType === "password") return 128;
    if (fieldType === "name") return 50;
    if (fieldType === "phone") return 15;
    if (fieldType === "age") return 3;
    if (fieldType === "address") return 200;
    if (fieldType === "bio") return 500;
    return max || maxLength;
  };

  const shouldShowPasswordStrength =
    showPasswordStrength && fieldType === "password" && value && isTouched;

  return (
    <FormControl isInvalid={isError} isRequired={required}>
      {label && (
        <FormLabel fontSize="sm" fontWeight="500" mb={2}>
          {label}
          {required && (
            <Text as="span" color="red.500" fontSize="sm" ml={1}>
              *
            </Text>
          )}
        </FormLabel>
      )}

      <InputGroup>
        {prefix && (
          <InputLeftElement pointerEvents="none" color="gray.500" fontSize="sm">
            {prefix}
          </InputLeftElement>
        )}

        <Input
          name={name}
          type={type}
          inputMode={inputMode}
          value={value || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          onPaste={handlePaste}
          placeholder={placeholder}
          disabled={disabled}
          min={min}
          max={max}
          step={step}
          maxLength={getMaxLength()}
          borderColor={isError ? "red.500" : isValid ? "green.500" : "inherit"}
          _focusVisible={{
            borderColor: isError ? "red.500" : "blue.500",
          }}
          paddingLeft={prefix ? "35px" : "12px"}
          h="40px"
          {...rest}
        />

        {isValid && !isError && (
          <InputRightElement pointerEvents="none" color="green.500">
            <CheckCircleIcon boxSize={4} />
          </InputRightElement>
        )}

        {isError && (
          <InputRightElement pointerEvents="none" color="red.500">
            <WarningIcon boxSize={4} />
          </InputRightElement>
        )}
      </InputGroup>

      {/* Error messages */}
      {isError && (externalError || internalError) && (
        <FormErrorMessage fontSize="sm" mt={1}>
          {externalError || internalError}
        </FormErrorMessage>
      )}

      {/* Helper text */}
      {helperText && !isError && (
        <FormHelperText fontSize="xs" color="gray.500" mt={1}>
          {helperText}
        </FormHelperText>
      )}

      {/* Password strength indicator */}
      {shouldShowPasswordStrength && (
        <Box mt={2}>
          <HStack spacing={2} mb={1}>
            <Text fontSize="xs" color="gray.600" fontWeight="500">
              Strength:
            </Text>
            <HStack spacing={1}>
              {[1, 2, 3, 4, 5].map((segment) => (
                <Box
                  key={segment}
                  w="8px"
                  h="2px"
                  bg={
                    segment <= passwordStrength.score
                      ? passwordStrength.level === "weak"
                        ? "red.500"
                        : passwordStrength.level === "medium"
                        ? "yellow.500"
                        : "green.500"
                      : "gray.300"
                  }
                  borderRadius="1px"
                  transition="all 0.2s"
                />
              ))}
            </HStack>
            <Text
              fontSize="xs"
              fontWeight="600"
              color={
                passwordStrength.level === "weak"
                  ? "red.500"
                  : passwordStrength.level === "medium"
                  ? "yellow.600"
                  : "green.600"
              }
            >
              {passwordStrength.level.charAt(0).toUpperCase() + passwordStrength.level.slice(1)}
            </Text>
          </HStack>
        </Box>
      )}

      {/* Validation rules display */}
      {validationRules.length > 0 && !isError && isTouched && (
        <Box mt={2} fontSize="xs" color="gray.600">
          <Text fontWeight="500" mb={1}>
            Requirements:
          </Text>
          <ul style={{ paddingLeft: "20px", margin: 0 }}>
            {validationRules.map((rule, idx) => (
              <li key={idx}>{rule}</li>
            ))}
          </ul>
        </Box>
      )}

      {/* Character count for fields with max length */}
      {getMaxLength() && value && (
        <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
          {String(value).length} / {getMaxLength()}
        </Text>
      )}
    </FormControl>
  );
};

ValidatedInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  type: PropTypes.string,
  fieldType: PropTypes.oneOf([
    "text",
    "name",
    "email",
    "phone",
    "age",
    "number",
    "currency",
    "password",
    "url",
    "date",
    "address",
    "bio",
  ]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.string,
  isTouched: PropTypes.bool,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  prefix: PropTypes.string,
  min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  step: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  inputMode: PropTypes.string,
  validationRules: PropTypes.arrayOf(PropTypes.string),
  validationOptions: PropTypes.object,
  showPasswordStrength: PropTypes.bool,
};

ValidatedInput.defaultProps = {
  label: null,
  value: "",
  type: "text",
  fieldType: "text",
  onBlur: null,
  error: null,
  isTouched: false,
  placeholder: "",
  helperText: null,
  prefix: null,
  min: null,
  max: null,
  step: null,
  required: false,
  disabled: false,
  inputMode: null,
  validationRules: [],
  validationOptions: {},
  showPasswordStrength: false,
};

export default ValidatedInput;
